import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { DefaultCatchBoundary } from "./components/DefaultCatchBoundary";
import { NotFound } from "./components/NotFound";
import { RouteLoadingIndicator } from "./components/RouteLoadingIndicator";

/**
 * Router configuration — controls SSR and SPA navigation behavior.
 *
 * Data fetching modes:
 *   SSR (direct URL):     Server runs loader → data embedded in HTML → client hydrates
 *   SPA (client link):    Client calls server function via RPC → deserializes response
 *   Preload (hover):      Same as SPA but triggered on hover intent, cached for navigation
 *
 * Error handling:
 *   Navigation errors (deserialization failures) → DefaultCatchBoundary renders null
 *   and triggers a hard reload to the target URL, falling back to SSR.
 *   Genuine errors → DefaultCatchBoundary renders error UI with retry/back buttons.
 *
 * Pending state:
 *   During SPA transitions, the previous page stays visible. After defaultPendingMs,
 *   a subtle top-of-page loading bar appears via RouteLoadingIndicator.
 *
 * Caching:
 *   defaultStaleTime keeps loader data fresh for 30s, avoiding re-fetches on
 *   back/forward navigation and repeated visits.
 */
export function getRouter() {
  return createRouter({
    routeTree,

    // Preload route data on hover intent for faster SPA navigation
    defaultPreload: "intent",

    // Error handling — see DefaultCatchBoundary for SPA error recovery strategy
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <NotFound />,

    // SPA transition: show loading indicator after 150ms delay
    // (avoids flash for fast loads, gives feedback for slow ones)
    defaultPendingComponent: RouteLoadingIndicator,
    defaultPendingMs: 150,
    defaultPendingMinMs: 200,

    // Cache loader data for 30s — prevents re-fetching on back/forward
    // and avoids redundant server function calls for recently visited pages
    defaultStaleTime: 30_000,

    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
